import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';

// "Which cart?" — the second question before a picking walk (after "Who is
// picking?"). The warehouse owns two carts, one for 4 boxes and one for 2
// (Fernando, 2026-09-16); the plan splits the aisles into trips by the cart's
// box count. Not tied to a tablet account: the tablets swap when one is on the
// charger. The last cart chosen on THIS tablet is offered first and is the
// default, so it is one tap; with a single cart configured the step never shows.
//
// Carts live in Settings → Warehouse → SmartPicking (warehouse_layout.picking.carts).

export interface Cart { id: string; name: string; boxes: number }

const KEY = 'wms_cart_id';

export function rememberedCartId(): string | null {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

interface Props {
  orderCount: number;
  onReady: (cart: Cart | null) => void;
  onCancel: () => void;
}

export default function WhichCart({ orderCount, onReady, onCancel }: Props) {
  const [carts, setCarts] = useState<Cart[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    authFetch(`${API}/warehouse-layout`)
      .then(r => (r.ok ? r.json() : {}) as Promise<any>)
      .then((cfg: any) => {
        if (!alive) return;
        const list: Cart[] = Array.isArray(cfg?.picking?.carts) ? cfg.picking.carts : [];
        if (list.length <= 1) { onReady(list[0] || null); return; }   // nothing to ask
        const last = rememberedCartId();
        setCarts([...list].sort((a, b) => (a.id === last ? -1 : b.id === last ? 1 : 0)));
      })
      .catch(() => { if (alive) setError('Could not load the carts'); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = (c: Cart) => {
    try { localStorage.setItem(KEY, c.id); } catch { /* ignore */ }
    onReady(c);
  };

  const last = rememberedCartId();
  return (
    <div className="fixed inset-0 bg-[#0f1117] z-50 flex flex-col">
      <div className="flex-none bg-[#1a1d27] border-b border-gray-800 px-6 py-4 flex items-center gap-4">
        <button onClick={onCancel} className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">✕ Cancel</button>
        <span className="text-white font-bold">Which cart?</span>
        <span className="text-gray-500 text-sm">{orderCount} orders</span>
      </div>
      <div className="flex-1 overflow-y-auto px-8 py-8">
        {error && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2 max-w-lg mx-auto text-center mb-6">{error}</div>}
        {carts === null && !error && <div className="text-gray-400 text-center mt-20">Loading carts…</div>}
        {carts && (
          <div className="max-w-3xl mx-auto">
            <div className="text-gray-500 text-xs uppercase tracking-widest mb-3">
              {last ? 'Last time on this tablet first' : 'Pick the cart you are taking'}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {carts.map(c => (
                <button key={c.id} onClick={() => choose(c)}
                  className={`py-8 rounded-2xl text-white ${c.id === last
                    ? 'bg-blue-600 hover:bg-blue-500'
                    : 'bg-gray-800 hover:bg-gray-700 border border-gray-700'}`}>
                  <div className="text-3xl font-black">{c.name}</div>
                  <div className={`text-lg mt-1 ${c.id === last ? 'text-blue-100' : 'text-gray-400'}`}>
                    {c.boxes} {c.boxes === 1 ? 'box' : 'boxes'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
